/*
  Photos drifting inside their frames as the page scrolls — the hero photo, the
  craftsmanship cards, the 'Numbered by hand' photos and the about photo.

  The frame travels with the page as normal; the photo inside it travels a
  little slower, so it seems to sit a layer behind the card. The photo is drawn
  taller than its frame (see the section's own styles) and the extra is what it
  slides through, so no edge ever shows. With the card in the middle of the
  screen the photo is centred, exactly as it is without the effect.

  Nothing is held: the drift is read straight off the scroll position, so it
  follows the page's own smooth scrolling. Photos that share a row on desktop
  drift together as one band.

  A section opts in with data-parallax, and marks each frame with
  data-parallax-frame; the photo is the frame's first child. Two optional dials
  on the section:

  - data-parallax-travel, in pixels, for a section that wants more or less
    reach than the rest. The overhang the section draws is still the ceiling.
  - data-parallax-anchor="top", for a section already on screen when the page
    opens — the hero. It is never seen coming in from the bottom, so its photo
    starts where the visitor starts, framed as drawn, and drifts one way as the
    section leaves. Everything further down the page keeps the two-way drift
    around its centre.

  Which way that one drift goes is not a setting: it goes wherever the section
  drew the room. Room above the frame and the photo falls behind the page,
  reading as a layer underneath it, which is what every section here asks for.
  Room below it alone and the photo leads the page up instead, reading as a
  layer in front — available to a frame whose photograph cannot spare anything
  off its top, at the price of being the opposite effect.

  With no JavaScript, or with reduced motion, the photos simply stand still.
*/
(function () {
  /* How far a photo drifts either side of centre over the card's whole trip
     up the screen, in pixels. */
  var TRAVEL = 20;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(section) {
    if (section.dataset.parallaxReady === 'true') return;
    section.dataset.parallaxReady = 'true';

    var frames = Array.prototype.slice.call(section.querySelectorAll('[data-parallax-frame]'));
    if (!frames.length) return;

    var travel = Number(section.dataset.parallaxTravel) || TRAVEL;
    var fromTop = section.dataset.parallaxAnchor === 'top';

    var last = [];

    function update() {
      var vh = window.innerHeight || 1;
      for (var i = 0; i < frames.length; i++) {
        var photo = frames[i].firstElementChild;
        if (!photo) continue;
        /* A frame that is not laid out — a review held back for 'load more',
           or one hidden on this screen size — has nothing to drift yet. */
        if (!frames[i].offsetHeight) continue;

        var offset = 0;
        if (!reduced.matches) {
          var box = frames[i].getBoundingClientRect();
          /*
            How far the photo is drawn past its frame, measured on each side
            rather than assumed to be the same on both: a section can draw the
            room above, below, or only on one of the two. A pixel is kept back
            everywhere, so no edge ever shows however short a narrow window
            makes the frame.
          */
          var above = -photo.offsetTop;
          var below = photo.offsetHeight + photo.offsetTop - frames[i].offsetHeight;

          if (fromTop) {
            /* 0 with the frame where the page opens, 1 as its bottom leaves
               the top of the screen. */
            var leaving = (box.height - box.bottom) / box.height;
            if (leaving < 0) leaving = 0;
            if (leaving > 1) leaving = 1;

            /*
              The drift goes where the room is. Room above and the photo falls
              behind the page, drifting down through it. No room above and it
              leads instead, drifting up through the room below.
            */
            var lag = above >= 1;
            var oneWay = Math.min(travel, (lag ? above : below) - 1);
            if (oneWay < 0) oneWay = 0;
            offset = Math.round((lag ? 1 : -1) * leaving * oneWay * 10) / 10;
          } else {
            /* 0 as the frame's top enters at the bottom of the screen, 1 as
               its bottom leaves at the top. */
            var progress = (vh - box.top) / (vh + box.height);
            if (progress < 0) progress = 0;
            if (progress > 1) progress = 1;
            /* Both ways around the centre, so it is the shorter of the two
               rooms that says how far. */
            var bothWays = Math.min(travel, Math.min(above, below) - 1);
            if (bothWays < 0) bothWays = 0;
            /* Lagging behind the page: the photo sits high in its frame on the
               way in and low on the way out. */
            offset = Math.round((progress * 2 - 1) * bothWays * 10) / 10;
          }
        }

        if (last[i] !== offset) {
          photo.style.transform = 'translate3d(0, ' + offset + 'px, 0)';
          last[i] = offset;
        }
      }
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    /* Only listens while the photos are anywhere near the screen. */
    var watching = false;
    function watch(on) {
      if (on === watching) return;
      watching = on;
      if (on) {
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
      } else {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
      update();
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          watch(entries[0].isIntersecting);
        },
        { rootMargin: '25% 0px' }
      ).observe(section);
    } else {
      watch(true);
    }

    if (reduced.addEventListener) reduced.addEventListener('change', update);
    update();
  }

  function start() {
    var sections = document.querySelectorAll('[data-parallax]');
    for (var i = 0; i < sections.length; i++) init(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
})();
